/**
 * brandColors.ts — the ONE place brand color literals may live outside
 * globals.css (design-system rule, style.txt §TOKEN GOVERNANCE v5).
 *
 * CSS-rendered UI must ALWAYS use the CSS custom properties / Tailwind tokens
 * (`var(--primary)`, `bg-primary`, …) so light/dark, org theming and the
 * accessibility hatches all track automatically.
 *
 * These constants exist only for contexts that cannot read CSS variables:
 *   - next/og image generation (opengraph-image.tsx runs in the edge renderer)
 *   - PDF / print document rendering (pdfkit, DocRenderer palettes)
 *   - transactional email HTML (no stylesheet, no custom properties)
 *   - canvas / chart exports rendered off-DOM
 *
 * Values MUST mirror globals.css. If you change a hue there, change it here.
 */

/** Apple system blue — the brand primary (light theme). */
export const PRIMARY = "#007aff";
/** Gradient end / hover fill for the primary. */
export const PRIMARY_DARK = "#0051d5";
/** Dark-theme primary (Apple dark-mode blue). */
export const PRIMARY_DARK_THEME = "#0a84ff";
/** The 135° brand gradient used by the logo and hero CTAs. */
export const GRADIENT = { from: PRIMARY, to: PRIMARY_DARK, angle: 135 } as const;

/** Dark chrome shell (top bars, dark CTA bands). */
export const SHELL = "#1f2937";

/** Ink for drawn/typed e-signatures. Fixed in every theme: the image is
 * stamped onto a white PDF page, so it must stay dark on paper. */
export const SIGNATURE_INK = "#0b1f4a";

/** Semantic status colors — light theme. */
export const STATUS = {
  success: "#34c759",
  warning: "#ff9500",
  destructive: "#ff3b30",
  info: "#007aff",
} as const;

/** Semantic status colors — dark theme. */
export const STATUS_DARK = {
  success: "#30d158",
  warning: "#ff9f0a",
  destructive: "#ff453a",
  info: "#0a84ff",
} as const;

/** Surface colors — light theme. */
export const SURFACE = {
  background: "#f5f5f7",
  foreground: "#1d1d1f",
  card: "#ffffff",
  muted: "#f2f2f7",
  mutedForeground: "#6e6e73",
  border: "#e5e5ea",
} as const;

/** Surface colors — dark theme. */
export const SURFACE_DARK = {
  background: "#1c1c1e",
  foreground: "#f5f5f7",
  card: "#2c2c2e",
  muted: "#2c2c2e",
  mutedForeground: "#98989d",
  border: "rgba(255,255,255,0.10)",
} as const;

/**
 * Categorical chart ramp (8 steps) — light theme. Mirrors --chart-1..8.
 * Assign by SERIES KEY (see chartColor()), never by array index: a re-sorted
 * dataset must not recolour its series.
 */
export const CHART = [
  "#007aff", // 1 blue
  "#34c759", // 2 green
  "#af52de", // 3 purple
  "#ff9500", // 4 orange
  "#ff3b30", // 5 red
  "#5856d6", // 6 indigo
  "#ff2d55", // 7 pink
  "#30b0c7", // 8 teal
] as const;

/** Categorical chart ramp — dark theme. Mirrors .dark --chart-1..8. */
export const CHART_DARK = [
  "#0a84ff",
  "#30d158",
  "#bf5af2",
  "#ff9f0a",
  "#ff453a",
  "#5e5ce6",
  "#ff375f",
  "#40cbe0",
] as const;

/**
 * Stable colour for a chart series, keyed by its identity string.
 * Same key → same hue on every render, in every order, in both themes.
 */
export function chartColor(seriesKey: string, dark = false): string {
  const ramp = dark ? CHART_DARK : CHART;
  let hash = 0;
  for (let i = 0; i < seriesKey.length; i++) {
    hash = (hash * 31 + seriesKey.charCodeAt(i)) >>> 0;
  }
  return ramp[hash % ramp.length];
}

/**
 * Swatches offered by every colour PICKER in the app — the Configuration tab's
 * list-item colours and the settings gear's `color` fields (MODULE_RULES §23).
 *
 * These are literals on purpose: the value is PERSISTED as a hex string (the
 * API validates `#rrggbb`), so a theme token would not survive the round trip.
 * Keeping them here rather than in a .tsx is what keeps literal colour out of
 * components, and gives both pickers one palette instead of two copies.
 */
export const PICKER_SWATCHES = [
  "#6366f1", // indigo
  "#3b82f6", // blue
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#64748b", // slate
] as const;

/**
 * The gradient stops the Business360 MARK is drawn with.
 *
 * Resolved 2026-08-19: the mark now follows the brand gradient (system blue,
 * per style.txt) — the SVG files' historical violet→pink was retired and
 * `public/logo-mark.svg`, `logo-wordmark.svg` and `favicon.svg` were redrawn
 * with these stops. If GRADIENT changes hue, change the SVGs and this pair
 * together; BrandMark reads MARK_GRADIENT and must stay identical to the files.
 */
export const MARK_GRADIENT = { from: PRIMARY, to: PRIMARY_DARK } as const;
